import { useArticles } from "@faire/help-center-utils/hooks/article/useArticles";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Account } from "@faire/web--source/slate/icons/foundation/Account";
import React from "react";

import { OTHER_NODES_LABEL } from "../../shared/other-node";
import { SelfServeNodes } from "../../types";
import { AccountEmailIA } from "../instantAnswers/myAccount-accountEmail";
import { ApplyForEoriIA } from "../instantAnswers/myAccount-applyForEori";
import { CheckAccountVerificationStatusIA } from "../instantAnswers/myAccount-checkAccountVerificationStatus";
import { ContactInfoIA } from "../instantAnswers/myAccount-contactInfo";
import { DoNotHaveRequestedInfoIA } from "../instantAnswers/myAccount-doNotHaveReuqestedInfo";
import { EmailNotificationsIA } from "../instantAnswers/myAccount-emailNotifications";
import { LinkingPosIA } from "../instantAnswers/myAccount-linkingPos";
import { ManageEoriInfoIA } from "../instantAnswers/myAccount-manageEoriInfo";
import { ManageTeamsIA } from "../instantAnswers/myAccount-manageTeams";
import { MultifactorAuthIA } from "../instantAnswers/myAccount-multifactorAuth";
import { PasswordIA } from "../instantAnswers/myAccount-password";
import { PasswordResetIA } from "../instantAnswers/myAccount-passwordReset";
import { ProvideRequestedInfoIA } from "../instantAnswers/myAccount-provideRequestedInfo";
import { ShippingAddressIA } from "../instantAnswers/myAccount-shippingAddress";
import { TroubleshootingAndOtherPOSIA } from "../instantAnswers/myAccount-troubleshootingAndOtherPos";
import { UnlinkingPosIA } from "../instantAnswers/myAccount-unlinkingPos";
import { UpdateAccountTypeIA } from "../instantAnswers/myAccount-updateAccountType";
import { VatAndTaxIdentifiersIA } from "../instantAnswers/myAccount-vatAndTaxIdentifiers";
import { WhatIsEoriIA } from "../instantAnswers/myAccount-whatIsEori";
import { WhyOrderCancelledIA } from "../instantAnswers/myAccount-whyOrderCancelled";
import { WhyOrderOnHoldIA } from "../instantAnswers/myAccount-whyOrderOnHold";
import { BillingAddressIA } from "../instantAnswers/shared-billingAddress";
import { AccountVerificationPendingVisibilityFn } from "../visibilityFunctions/accountVerificationPendingVisibilityFn";
import { AccountVerificationRejectedVisibilityFn } from "../visibilityFunctions/accountVerificationRejectedVisibilityFn";
import { AccountVerificationVisibilityFn } from "../visibilityFunctions/accountVerificationVisibilityFn";
import { ManageEoriInfoVisibilityFn } from "../visibilityFunctions/manageEoriInfoVisibilityFn";
import { VatAndTaxIdentifiersVisibilityFn } from "../visibilityFunctions/vatAndTaxIdentifiersVisibilityFn";

export const useMyAccountRetailerNodes = (): SelfServeNodes => {
  const { strictLocalize } = useStrictLocalization();
  const articles = useArticles();
  return {
    MyAccount: {
      id: "MyAccount",
      label: strictLocalize({
        defaultMessage: "My account",
        description: {
          text: "A top level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      icon: <Account fontSize="24px" color="faireBlack" />,
      children: [
        "AccountVerification",
        "ManageAccount",
        "AccountSettings",
        "PointOfSale",
      ],
      level: 1,
      sidebarTitle: strictLocalize({
        defaultMessage: "Account issue",
        description: {
          text: "Title for displaying on the order summary sidebar",
        },
      }),
    },
    AccountVerification: {
      id: "AccountVerification",
      label: strictLocalize({
        defaultMessage: "Account verification",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "WhyOrderOnHold",
        "ProvideRequestedInformation",
        "DoNotHaveRequestedInformation",
        "CheckAccountVerificationStatus",
        "WhyOrderCancelled",
        "OtherAccountVerification",
      ],
      isNodeVisible: AccountVerificationVisibilityFn,
      level: 2,
    },

    ManageAccount: {
      id: "ManageAccount",
      label: strictLocalize({
        defaultMessage: "Manage account",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: ["AccountEmail", "ManageTeams", "EmailNotifications"],
      level: 2,
    },
    AccountSettings: {
      id: "AccountSettings",
      label: strictLocalize({
        defaultMessage: "Account settings",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "PasswordReset",
        "MultifactorAuth",
        "UpdateAccount",
        "OtherAccountSettings",
      ],
      level: 2,
    },
    PointOfSale: {
      id: "PointOfSale",
      label: strictLocalize({
        defaultMessage: "Point of sale",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "LinkingPOS",
        "Troubleshooting",
        "UnlinkingPOS",
        "OtherPointOfSale",
      ],
      level: 2,
    },
    WhyOrderOnHold: {
      id: "WhyOrderOnHold",
      label: strictLocalize({
        defaultMessage: "Why is my order on hold",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.MyOrders.BUSINESS_PROFILE],
      instantAnswer: WhyOrderOnHoldIA(strictLocalize, articles),
      isNodeVisible: AccountVerificationPendingVisibilityFn,
      level: 3,
    },
    ProvideRequestedInformation: {
      id: "ProvideRequestedInformation",
      label: strictLocalize({
        defaultMessage: "Provide requested information",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.Account.ORDER_ISSUE],
      instantAnswer: ProvideRequestedInfoIA(strictLocalize),
      isNodeVisible: AccountVerificationPendingVisibilityFn,
      level: 3,
    },
    DoNotHaveRequestedInformation: {
      id: "DoNotHaveRequestedInformation",
      label: strictLocalize({
        defaultMessage: "Do not have requested information",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.Account.NOT_OPEN],
      instantAnswer: DoNotHaveRequestedInfoIA(strictLocalize, articles),
      isNodeVisible: AccountVerificationPendingVisibilityFn,
      level: 3,
    },
    CheckAccountVerificationStatus: {
      id: "CheckAccountVerificationStatus",
      label: strictLocalize({
        defaultMessage: "Check account verification status",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.MyOrders.BUSINESS_PROFILE],
      instantAnswer: CheckAccountVerificationStatusIA(strictLocalize, articles),
      isNodeVisible: AccountVerificationPendingVisibilityFn,
      level: 3,
    },
    WhyOrderCancelled: {
      id: "WhyOrderCancelled",
      label: strictLocalize({
        defaultMessage: "Why is my order cancelled",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: WhyOrderCancelledIA(strictLocalize, articles),
      isNodeVisible: AccountVerificationRejectedVisibilityFn,
      level: 3,
    },
    OtherAccountVerification: {
      id: "OtherAccountVerification",
      label: OTHER_NODES_LABEL(strictLocalize),
      articles: [articles.MyOrders.BUSINESS_PROFILE],
      isNodeVisible: AccountVerificationPendingVisibilityFn,
      level: 3,
    },
    AccountEmail: {
      id: "AccountEmail",
      label: strictLocalize({
        defaultMessage: "Account email",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.Account.ACCOUNT_EMAIL],
      instantAnswer: AccountEmailIA(strictLocalize),
      level: 3,
    },
    ManageTeams: {
      id: "ManageTeams",
      label: strictLocalize({
        defaultMessage: "Manage teams",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.Account.MANAGE_TEAMS],
      instantAnswer: ManageTeamsIA(strictLocalize),
      level: 3,
    },
    EmailNotifications: {
      id: "EmailNotifications",
      label: strictLocalize({
        defaultMessage: "Email notifications",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.Account.EMAIL_NOTIFICATIONS],
      instantAnswer: EmailNotificationsIA(strictLocalize),
      level: 3,
    },
    PasswordReset: {
      id: "PasswordReset",
      label: strictLocalize({
        defaultMessage: "Password reset",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.FAQ.RESETTING_PASSWORD],
      instantAnswer: PasswordResetIA(strictLocalize),
      level: 3,
    },
    MultifactorAuth: {
      id: "MultifactorAuth",
      label: strictLocalize({
        defaultMessage: "Multifactor authentication",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: MultifactorAuthIA(strictLocalize),
      level: 3,
    },
    UpdateAccount: {
      id: "UpdateAccount",
      label: strictLocalize({
        defaultMessage: "Update account",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "BillingAddress",
        "ShippingAddress",
        "ContactInfo",
        "EORI",
        "Password",
        "VATAndTaxIdentifiers",
        "UpdateAccountType",
      ],
      level: 3,
    },
    OtherAccountSettings: {
      id: "OtherAccountSettings",
      label: OTHER_NODES_LABEL(strictLocalize),
      articles: [
        articles.GettingStarted.RESELLER_ID,
        articles.GettingStarted.BRICK_AND_MORTAR,
        articles.MyOrders.BUSINESS_PROFILE,
      ],
      level: 3,
    },
    LinkingPOS: {
      id: "LinkingPOS",
      label: strictLocalize({
        defaultMessage: "Linking POS",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.MyOrders.SYNC_SQUARE_POS],
      instantAnswer: LinkingPosIA(strictLocalize),
      level: 3,
    },
    Troubleshooting: {
      id: "Troubleshooting",
      label: strictLocalize({
        defaultMessage: "Troubleshooting",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.MyOrders.SYNC_SQUARE_POS],
      instantAnswer: TroubleshootingAndOtherPOSIA(strictLocalize),
      level: 3,
    },
    UnlinkingPOS: {
      id: "UnlinkingPOS",
      label: strictLocalize({
        defaultMessage: "Unlinking POS",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.MyOrders.SYNC_SQUARE_POS],
      instantAnswer: UnlinkingPosIA(strictLocalize),
      level: 3,
    },
    OtherPointOfSale: {
      id: "OtherPointOfSale",
      label: OTHER_NODES_LABEL(strictLocalize),
      articles: [articles.MyOrders.SYNC_SQUARE_POS],
      instantAnswer: TroubleshootingAndOtherPOSIA(strictLocalize),
      level: 3,
    },
    BillingAddress: {
      id: "BillingAddress",
      label: strictLocalize({
        defaultMessage: "Billing address",
        description: {
          text: "A fourth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [
        articles.PaymentsAndInvoices.HOW_DO_I_CHANGE_MY_BILLING_ADDRESS,
      ],
      instantAnswer: BillingAddressIA(strictLocalize),
      level: 4,
    },
    ShippingAddress: {
      id: "ShippingAddress",
      label: strictLocalize({
        defaultMessage: "Shipping address",
        description: {
          text: "A fourth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.FulfillingOrders.SHIPPING_ADDRESS],
      instantAnswer: ShippingAddressIA(strictLocalize),
      level: 4,
    },
    ContactInfo: {
      id: "ContactInfo",
      label: strictLocalize({
        defaultMessage: "Contact information",
        description: {
          text: "A fourth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: ContactInfoIA(strictLocalize),
      level: 4,
    },
    EORI: {
      id: "EORI",
      label: strictLocalize({
        defaultMessage: "EORI",
        description: {
          text: "A fourth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: ["WhatIsEORI", "ApplyForEORI", "ManageEORI"],
      level: 4,
    },
    Password: {
      id: "Password",
      label: strictLocalize({
        defaultMessage: "Password",
        description: {
          text: "A fourth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: PasswordIA(strictLocalize),
      level: 4,
    },
    VATAndTaxIdentifiers: {
      id: "VATAndTaxIdentifiers",
      label: strictLocalize({
        defaultMessage: "VAT and tax identifiers",
        description: {
          text: "A fourth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.ImportFeesAndDuties.VAT],
      instantAnswer: VatAndTaxIdentifiersIA(strictLocalize),
      isNodeVisible: VatAndTaxIdentifiersVisibilityFn,
      level: 4,
    },
    UpdateAccountType: {
      id: "UpdateAccountType",
      label: strictLocalize({
        defaultMessage: 'Update account type to "Brick and mortar"',
        description: {
          text: "A fourth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: UpdateAccountTypeIA(strictLocalize),
      level: 4,
    },
    WhatIsEORI: {
      id: "WhatIsEORI",
      label: strictLocalize({
        defaultMessage: "What is EORI number",
        description: {
          text: "A fifth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.ImportFeesAndDuties.EORI],
      instantAnswer: WhatIsEoriIA(strictLocalize),
      level: 5,
    },
    ApplyForEORI: {
      id: "ApplyForEORI",
      label: strictLocalize({
        defaultMessage: "Apply for EORI number",
        description: {
          text: "A fifth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: ApplyForEoriIA(strictLocalize, articles),
      isNodeVisible: ManageEoriInfoVisibilityFn,
      level: 5,
    },
    ManageEORI: {
      id: "ManageEORI",
      label: strictLocalize({
        defaultMessage: "Manage EORI information",
        description: {
          text: "A fifth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: ManageEoriInfoIA(strictLocalize),
      isNodeVisible: ManageEoriInfoVisibilityFn,
      level: 5,
    },
  };
};
